import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Download,
  FileUp,
  Loader2,
  Save,
} from "lucide-react";
import {
  BOOKING_STATUS_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
} from "../config";
import {
  calculateAdminPricing,
  canChangeBookingStatus,
  findBookingConflicts,
} from "../bookingUtils";
import {
  changePhoenixBookingStatus,
  getPrivateDocumentUrl,
  subscribePhoenixAuditLog,
  subscribePhoenixBlocks,
  subscribePhoenixBooking,
  subscribePhoenixBookings,
  subscribePhoenixSpaces,
  updatePhoenixBooking,
  uploadPhoenixInternalDocument,
} from "../service";
import {
  AdminPageHeader,
  displaySpaceNames,
  formatTimestamp,
  PhoenixAdminNav,
  StatusBadge,
} from "./AdminShared";

const emptyPricing = {
  hourlyRate: 0,
  chargeableHours: 0,
  cleaningCharge: 0,
  staffingCharge: 0,
  equipmentCharge: 0,
  depositRequired: 0,
  discount: 0,
  otherCharges: 0,
  amountPaid: 0,
  paymentStatus: "not_requested",
  paymentMethod: "",
  paymentDate: "",
  transactionReference: "",
};

function YesNoValue({ value }) {
  return <>{value === true ? "Yes" : value === false ? "No" : "Not supplied"}</>;
}

export default function PhoenixBookingDetail() {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [audit, setAudit] = useState([]);
  const [pricing, setPricing] = useState(emptyPricing);
  const [adminNotes, setAdminNotes] = useState("");
  const [customerNotes, setCustomerNotes] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [nextStatus, setNextStatus] = useState("");
  const [statusReason, setStatusReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [internalFile, setInternalFile] = useState(null);

  useEffect(() => {
    const unsubscribers = [
      subscribePhoenixBooking(id, setBooking, () => setError("This booking could not be loaded.")),
      subscribePhoenixBookings(setBookings, () => {}),
      subscribePhoenixBlocks(setBlocks, () => {}),
      subscribePhoenixSpaces(setSpaces, () => {}),
      subscribePhoenixAuditLog(id, setAudit, () => {}),
    ];
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [id]);

  useEffect(() => {
    if (!booking) return;
    setPricing({ ...emptyPricing, ...(booking.pricing || {}) });
    setAdminNotes(booking.adminNotes || "");
    setCustomerNotes(booking.customerNotes || "");
    setAssignedTo(booking.assignedTo || "");
    setNextStatus(booking.status || "submitted");
  }, [booking]);

  const conflicts = useMemo(
    () => (booking ? findBookingConflicts(booking, bookings, blocks) : { hasConflict: false, bookingConflicts: [], blockConflicts: [] }),
    [booking, bookings, blocks]
  );

  const calculatedPricing = useMemo(() => calculateAdminPricing(pricing), [pricing]);

  const saveDetails = async () => {
    setSaving(true); setError(""); setMessage("");
    try {
      await updatePhoenixBooking(id, {
        pricing: calculatedPricing,
        adminNotes,
        customerNotes,
        assignedTo,
        conflictStatus: conflicts.hasConflict ? "possible_conflict" : "clear",
        auditNote: "Pricing, notes or assignment updated.",
      }, "admin_details_updated");
      setMessage("Booking details saved.");
    } catch (saveError) {
      setError(saveError?.message || "The booking could not be saved.");
    } finally { setSaving(false); }
  };

  const applyStatus = async () => {
    setError(""); setMessage("");
    if (!canChangeBookingStatus(booking.status, nextStatus)) {
      setError(`The booking cannot move directly from ${booking.status} to ${nextStatus}.`);
      return;
    }
    setSaving(true);
    try {
      await changePhoenixBookingStatus(booking, nextStatus, statusReason);
      setStatusReason("");
      setMessage("Booking status updated and a notification was queued.");
    } catch (statusError) {
      setError(statusError?.message || "The status could not be changed.");
    } finally { setSaving(false); }
  };

  const openDocument = async (document) => {
    try {
      const url = await getPrivateDocumentUrl(document.path || document);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      setError("The document could not be opened. Check Storage rules and your admin access.");
    }
  };

  const uploadInternal = async () => {
    if (!internalFile) return;
    setSaving(true); setError("");
    try {
      const path = await uploadPhoenixInternalDocument(id, internalFile);
      await updatePhoenixBooking(id, {
        internalDocumentPaths: [...(booking.internalDocumentPaths || []), path],
        auditNote: `Internal document uploaded: ${internalFile.name}`,
      }, "internal_document_uploaded");
      setInternalFile(null);
      setMessage("Internal document uploaded.");
    } catch (uploadError) {
      setError(uploadError?.message || "The internal document could not be uploaded.");
    } finally { setSaving(false); }
  };

  if (!booking && !error) return <div className="phx-shell phx-admin-shell"><p className="phx-loading" role="status">Loading booking…</p></div>;
  if (!booking) return <div className="phx-shell phx-admin-shell"><div className="phx-submit-error">{error || "Booking not found."}</div><Link to="/admin/phoenix-bookings">Back to bookings</Link></div>;
  const safeguardingDetailsRequired =
    booking.attendance?.childrenOrVulnerableAdults === true ||
    booking.safeguarding?.proceduresRequired === true;

  return (
    <div className="phx-shell phx-admin-shell">
      <PhoenixAdminNav />
      <Link className="phx-back-link" to="/admin/phoenix-bookings"><ArrowLeft aria-hidden="true" /> All bookings</Link>
      <AdminPageHeader eyebrow={booking.bookingReference} title={booking.activityDetails?.name || "Booking request"}>
        <div className="phx-admin-actions"><StatusBadge status={booking.status} />{booking.conflictStatus === "possible_conflict" ? <span className="phx-conflict"><AlertTriangle aria-hidden="true" /> Possible conflict</span> : null}</div>
      </AdminPageHeader>
      {error ? <div className="phx-submit-error" role="alert">{error}</div> : null}
      {message ? <div className="phx-availability-available" role="status">{message}</div> : null}

      {conflicts.hasConflict ? <section className="phx-admin-card phx-conflict-panel"><h2><AlertTriangle aria-hidden="true" /> Scheduling conflicts need review</h2><p>{conflicts.bookingConflicts.length} booking conflict(s) and {conflicts.blockConflicts.length} blocked-period conflict(s) were detected. Compare dates below before approving.</p>{conflicts.bookingConflicts.map((item) => <Link key={item.id} to={`/admin/phoenix-bookings/${item.id}`}>{item.bookingReference} — {item.activityDetails?.name}</Link>)}</section> : null}

      <div className="phx-admin-grid">
        <section className="phx-admin-card"><h2>Contact and organisation</h2><dl className="phx-admin-detail-list"><div><dt>Full name</dt><dd>{booking.contact?.fullName}</dd></div><div><dt>Organisation</dt><dd>{booking.organisation?.name || "—"}</dd></div><div><dt>Email</dt><dd><a href={`mailto:${booking.contact?.email}`}>{booking.contact?.email}</a></dd></div><div><dt>Telephone</dt><dd><a href={`tel:${booking.contact?.telephone}`}>{booking.contact?.telephone}</a></dd></div><div><dt>Preferred contact</dt><dd>{booking.contact?.preferredContactMethod}</dd></div><div><dt>Address/postcode</dt><dd>{booking.contact?.postcode || "—"}</dd></div></dl></section>
        <section className="phx-admin-card"><h2>Space and dates</h2><p><strong>{displaySpaceNames(booking.selectedSpaceIds, spaces)}</strong> · {booking.bookingType?.replaceAll("_", " ")}</p>{booking.requestedDates?.length ? <ul className="phx-date-list">{booking.requestedDates.map((date, index) => <li key={`${date.date}-${index}`}><strong>{date.date} · {date.startTime}–{date.endTime}</strong><small>{formatTimestamp(date.startDateTime)} to {formatTimestamp(date.endDateTime)}</small></li>)}</ul> : <p>Dates to be discussed.</p>}</section>
      </div>

      <section className="phx-admin-card"><h2>Activity details</h2><dl className="phx-admin-detail-list"><div><dt>Description</dt><dd>{booking.activityDetails?.description}</dd></div><div><dt>Expected attendance</dt><dd>{booking.attendance?.expectedCount} · {booking.attendance?.ageRange}</dd></div><div><dt>Attendance type</dt><dd>{booking.activityDetails?.attendanceType}</dd></div><div><dt>Children/vulnerable adults</dt><dd><YesNoValue value={booking.attendance?.childrenOrVulnerableAdults} /></dd></div><div><dt>Admission fee</dt><dd><YesNoValue value={booking.activityDetails?.admissionFeeCharged} /></dd></div><div><dt>Commercial</dt><dd><YesNoValue value={booking.activityDetails?.commercialActivity} /></dd></div><div><dt>Food served</dt><dd><YesNoValue value={booking.activityDetails?.foodServed} /></dd></div><div><dt>Amplified sound</dt><dd><YesNoValue value={booking.activityDetails?.amplifiedSound} /></dd></div><div><dt>Specialist equipment</dt><dd><YesNoValue value={booking.activityDetails?.specialistEquipment} /></dd></div><div><dt>Setup / pack-down</dt><dd>{booking.activityDetails?.setupMinutes || 0} / {booking.activityDetails?.packDownMinutes || 0} minutes</dd></div><div><dt>Accessibility</dt><dd>{booking.activityDetails?.accessibilityRequirements || "—"}</dd></div><div><dt>Additional information</dt><dd>{booking.activityDetails?.additionalInformation || "—"}</dd></div></dl></section>

      <section className="phx-admin-card"><h2>Compliance and safeguarding</h2><dl className="phx-admin-detail-list"><div><dt>Public liability insurance</dt><dd><YesNoValue value={booking.insurance?.publicLiabilityHeld} /></dd></div><div><dt>Safeguarding procedures</dt><dd><YesNoValue value={booking.safeguarding?.proceduresRequired} /></dd></div><div><dt>Safeguarding policy</dt><dd>{safeguardingDetailsRequired ? <YesNoValue value={booking.safeguarding?.policyHeld} /> : "Not applicable"}</dd></div><div><dt>DBS required</dt><dd>{safeguardingDetailsRequired ? <YesNoValue value={booking.safeguarding?.dbsRequired} /> : "Not applicable"}</dd></div><div><dt>Licence required</dt><dd><YesNoValue value={booking.compliance?.licenceRequired} /></dd></div><div><dt>Storage required</dt><dd><YesNoValue value={booking.compliance?.storageRequired} /></dd></div><div><dt>UUN staff required</dt><dd><YesNoValue value={booking.compliance?.uunStaffRequired} /></dd></div></dl></section>

      <section className="phx-admin-card"><h2>Documents</h2><div className="phx-admin-grid"><div><h3>Submitted documents</h3>{booking.documentPaths?.length ? <ul className="phx-date-list">{booking.documentPaths.map((document) => <li key={document.path}><strong>{document.originalName}</strong><small>{document.contentType} · {Math.round(document.size / 1024)} KB</small><button type="button" className="phx-button phx-button-secondary" onClick={() => openDocument(document)}><Download aria-hidden="true" /> Open privately</button></li>)}</ul> : <p>No documents submitted.</p>}</div><div><h3>Internal documents</h3>{booking.internalDocumentPaths?.map((path) => <button type="button" key={path} className="phx-button phx-button-secondary" onClick={() => openDocument(path)}><Download aria-hidden="true" /> {path.split("/").pop()}</button>)}<div className="phx-admin-actions"><input type="file" accept=".pdf,.docx,.jpg,.jpeg,.png" onChange={(e) => setInternalFile(e.target.files?.[0] || null)} /><button type="button" className="phx-button" onClick={uploadInternal} disabled={!internalFile || saving}><FileUp aria-hidden="true" /> Upload internal file</button></div></div></div></section>

      <section className="phx-admin-card"><h2>Pricing and payment</h2><div className="phx-admin-form">{[
        ["hourlyRate", "Final hourly rate (£)"], ["chargeableHours", "Chargeable hours"], ["cleaningCharge", "Cleaning charge (£)"], ["staffingCharge", "Staffing charge (£)"], ["equipmentCharge", "Equipment charge (£)"], ["depositRequired", "Security deposit (£)"], ["discount", "Discount (£)"], ["otherCharges", "Other charges (£)"], ["amountPaid", "Amount paid (£)"],
      ].map(([key, label]) => <label key={key}>{label}<input type="number" min="0" step="0.01" value={pricing[key] ?? 0} onChange={(e) => setPricing((current) => ({ ...current, [key]: e.target.value }))} /></label>)}<label>Payment status<select value={pricing.paymentStatus} onChange={(e) => setPricing((current) => ({ ...current, paymentStatus: e.target.value }))}>{PAYMENT_STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label><label>Payment method<input value={pricing.paymentMethod || ""} onChange={(e) => setPricing((current) => ({ ...current, paymentMethod: e.target.value }))} placeholder="Bank transfer, cash, card…" /></label><label>Payment date<input type="date" value={pricing.paymentDate || ""} onChange={(e) => setPricing((current) => ({ ...current, paymentDate: e.target.value }))} /></label><label>Transaction reference<input value={pricing.transactionReference || ""} onChange={(e) => setPricing((current) => ({ ...current, transactionReference: e.target.value }))} /></label></div><dl className="phx-admin-detail-list"><div><dt>Subtotal</dt><dd>£{calculatedPricing.subtotal.toFixed(2)}</dd></div><div><dt>Additional charges</dt><dd>£{calculatedPricing.additionalCharges.toFixed(2)}</dd></div><div><dt>Total</dt><dd>£{calculatedPricing.totalAmount.toFixed(2)}</dd></div><div><dt>Balance due</dt><dd>£{calculatedPricing.balanceDue.toFixed(2)}</dd></div></dl></section>

      <section className="phx-admin-card"><h2>Notes and assignment</h2><div className="phx-admin-form"><label className="phx-full">Internal notes<textarea rows="5" value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} maxLength="5000" /></label><label className="phx-full">Customer-visible notes<textarea rows="5" value={customerNotes} onChange={(e) => setCustomerNotes(e.target.value)} maxLength="3000" /></label><label>Assigned administrator<input value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} placeholder="Name or email" /></label></div><div className="phx-admin-actions"><button type="button" className="phx-button" onClick={saveDetails} disabled={saving}>{saving ? <Loader2 className="phx-spin" aria-hidden="true" /> : <Save aria-hidden="true" />} Save pricing and notes</button></div></section>

      <section className="phx-admin-card"><h2>Booking status</h2><div className="phx-admin-form"><label>New status<select value={nextStatus} onChange={(e) => setNextStatus(e.target.value)}>{BOOKING_STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label><label className="phx-full">Reason or message {(["rejected", "cancelled"].includes(nextStatus)) ? "*" : "(optional)"}<textarea rows="3" value={statusReason} onChange={(e) => setStatusReason(e.target.value)} maxLength="1000" /></label></div><p>Changing status creates an audit entry. Customer notifications are queued for the configured delivery service.</p><div className="phx-admin-actions"><button type="button" className={`phx-button ${["rejected", "cancelled"].includes(nextStatus) ? "phx-danger-button" : ""}`} onClick={applyStatus} disabled={saving || nextStatus === booking.status}>{saving ? <Loader2 className="phx-spin" aria-hidden="true" /> : null} Apply status</button></div></section>

      <section className="phx-admin-card"><h2>Audit history</h2>{audit.length ? <ul className="phx-audit-list">{audit.map((entry) => <li key={entry.id}><strong>{entry.action?.replaceAll("_", " ")}</strong><small>{formatTimestamp(entry.createdAt)} · {entry.performedByEmail || entry.performedBy || "system"}</small>{entry.notes ? <p>{entry.notes}</p> : null}</li>)}</ul> : <p>No audit entries available.</p>}</section>
    </div>
  );
}
